import { useCallback, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Camera, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { ReactNode } from 'react'

import type { Product } from '@/lib/api/product-api'
import { MobileBarcodeScannerDialog } from '@/components/orders/mobile-barcode-scanner-dialog'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { FieldError } from '@/components/ui/field'
import { parseLotDiscountInput } from '@/lib/lot-discount'
import { PositionPicker } from '@/components/warehouse-positions/position-picker'
import { renderProductOption } from '@/components/products/product-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// ProductLotItemsField — multi-lot picker cho cả PNK + PXK lô sản phẩm.
//
// mode='in': mỗi row có toggle "Lô mới" / "Lô có sẵn"
// mode='out': chỉ pick lô có sẵn
//
// CRITICAL: dùng useFieldArray.update(idx, newValue) khi toggle mode row —
// KHÔNG dùng setValue(path, val) (anti-pattern gây orphan field cũ trong store).

// today() returns local-date `YYYY-MM-DD` (not toISOString — that shifts to UTC).
function today(): string {
    const d = new Date()
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${mo}-${dd}`
}

// addOneYear returns YYYY-MM-DD 1 năm sau `iso`. Bảo toàn ngày (leap-year adjust
// nếu vào 29/2).
function addOneYear(iso: string): string {
    if (!iso) return ''
    const [ys, ms, ds] = iso.split('-')
    const y = Number(ys)
    const mo = Number(ms)
    const d = Number(ds)
    if (!y || !mo || !d) return ''
    const dt = new Date(y + 1, mo - 1, d)
    // Nếu 29/2 → 28/2 năm sau (JS auto rollover to 1/3, cần adjust).
    if (dt.getMonth() !== mo - 1) dt.setDate(0)
    const ny = dt.getFullYear()
    const nm = String(dt.getMonth() + 1).padStart(2, '0')
    const nd = String(dt.getDate()).padStart(2, '0')
    return `${ny}-${nm}-${nd}`
}

export interface ProductOption {
    value: string
    label: string
    unit?: string
    /** render tuỳ chọn cho dropdown row (ảnh + tên + SKU · KLT · unit + badge
     *  tồn), giống picker màn tạo đơn. Không truyền → Combobox hiện `label`. */
    render?: () => ReactNode
}

export interface WarehouseOption {
    value: string
    label: string
}

/** render tuỳ chọn cho dropdown row (Combobox); trigger vẫn dùng `label`. */
export interface ProductLotOption {
    render?: () => ReactNode
    value: string
    label: string
    available?: number
    unit?: string
}

interface RowNew {
    kind: 'new'
    new_lot_code?: string
    new_lot_product_id: string
    new_lot_warehouse_id?: string
    new_lot_position_id?: string
    new_lot_manufactured_date?: string
    new_lot_expiry_date?: string
    /** Mức CK đánh dấu cho lô (0 = không đánh dấu). Ưu tiên chọn lô khi đơn CK khớp. */
    new_lot_discount_percent?: number
    quantity: number
}

interface RowExisting {
    kind: 'existing'
    product_lot_id: string
    quantity: number
}

export type ProductLotItemRow = RowNew | RowExisting

interface Props {
    mode: 'in' | 'out'
    productOptions: ProductOption[]
    lotOptions: ProductLotOption[]
    onProductSearchChange?: (search: string) => void
    /** Server-side search callback cho lot Combobox (mode='out'). Caller cần debounce. */
    onLotSearchChange?: (search: string) => void
    fieldName?: string
    maxItems?: number
    /** Extra buttons render bên trái nút "Thêm lô" trên header (vd: nút Quét mã lô). */
    headerAction?: ReactNode
    /** Render thêm UI dưới mỗi row (vd: picker lô lấy hương nguồn cho from_packaging). */
    renderExtraPerItem?: (idx: number) => ReactNode
    /** Extra lot options inject vào Combobox (mode='out') — dùng cho lot
     *  quét mã vạch mà nằm ngoài top N của useProductLotsQuery. Parent fetch
     *  lot by ID sau scan rồi truyền vào đây. Merge với lotOptions
     *  (scannedLots wins nếu trùng id). */
    scannedLots?: ProductLotOption[]
}

export function ProductLotItemsField({
    mode,
    productOptions,
    lotOptions,
    onProductSearchChange,
    onLotSearchChange,
    fieldName = 'items',
    maxItems,
    headerAction,
    scannedLots,
    renderExtraPerItem,
}: Props) {
    const [scannerRowIndex, setScannerRowIndex] = useState<number | null>(null)
    // Cache SP đã quét barcode HOẶC đã chọn tay để inject vào productOptions.
    //
    // `useProducts` là search server-side DÙNG CHUNG cho mọi row → productOptions
    // chỉ chứa kết quả của lần search hiện tại. Không cache thì SP đã chọn ở row A
    // biến mất khỏi trigger ngay khi search đổi (gõ tìm ở row B, hoặc mở lại picker
    // rồi xoá ô tìm → refetch top-50). Giá trị trong form vẫn đúng, nhưng trigger
    // show placeholder → user tưởng mất, chọn lại. Cache giữ SP đã chọn hiện diện
    // trong options ở MỌI row bất kể search hiện tại.
    const [scannedProducts, setScannedProducts] = useState<
        Map<string, ProductOption>
    >(new Map())
    const cacheProduct = useCallback((opt: ProductOption) => {
        setScannedProducts((prev) => {
            if (prev.has(opt.value)) return prev
            const next = new Map(prev)
            next.set(opt.value, opt)
            return next
        })
    }, [])
    // Cùng vấn đề cho ô chọn LÔ khi form dùng server-search lô (mode='out' PXK):
    // `scannedLots` prop chỉ giữ lô quét barcode. Lô chọn tay cần cache local,
    // nếu không nó biến mất khỏi trigger khi lotOptions đổi theo search dùng chung.
    const [pickedLots, setPickedLots] = useState<Map<string, ProductLotOption>>(
        new Map(),
    )
    const cacheLot = useCallback((opt: ProductLotOption) => {
        setPickedLots((prev) => {
            if (prev.has(opt.value)) return prev
            const next = new Map(prev)
            next.set(opt.value, opt)
            return next
        })
    }, [])
    const { control, register, watch, setValue } = useFormContext()
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: fieldName,
    })
    const scannerOpen = scannerRowIndex !== null
    // Đọc toàn bộ items để loại product_lot_id đã chọn ở row khác — tránh
    // user chọn cùng 1 lô 2 lần (approve sẽ 500 vì deduct vượt available).
    const allItems = watch(fieldName) as
        | Array<Record<string, unknown>>
        | undefined
    const selectedLotIds = useMemo(
        () =>
            (allItems ?? []).map(
                (row) => (row?.product_lot_id as string | undefined) ?? '',
            ),
        [allItems],
    )

    // Merge productOptions từ server (search) + SP đã quét (cache local).
    // Scanned wins nếu trùng id (giữ label/unit cache).
    const mergedProductOptions = useMemo<ProductOption[]>(() => {
        if (scannedProducts.size === 0) return productOptions
        const map = new Map<string, ProductOption>()
        for (const opt of productOptions) map.set(opt.value, opt)
        for (const [id, opt] of scannedProducts) map.set(id, opt)
        return Array.from(map.values())
    }, [productOptions, scannedProducts])

    // Merge lotOptions từ server + scannedLots (quét mã ở PXK) + pickedLots (chọn
    // tay). Cả 2 cache đều "wins" để lô đã dùng không rớt khỏi list khi search đổi.
    const mergedLotOptions = useMemo<ProductLotOption[]>(() => {
        if (
            (!scannedLots || scannedLots.length === 0) &&
            pickedLots.size === 0
        ) {
            return lotOptions
        }
        const map = new Map<string, ProductLotOption>()
        for (const opt of lotOptions) map.set(opt.value, opt)
        for (const opt of pickedLots.values()) map.set(opt.value, opt)
        for (const opt of scannedLots ?? []) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [lotOptions, scannedLots, pickedLots])

    const addRow = () => {
        if (mode === 'in') {
            const nsx = today()
            append({
                kind: 'new' as const,
                new_lot_product_id: '',
                // '' chứ không để undefined — xem chú thích ở stock-in-notes/new.tsx:
                // undefined làm z.string().uuid() abort item → superRefine bị bỏ qua.
                new_lot_position_id: '',
                new_lot_manufactured_date: nsx,
                new_lot_expiry_date: addOneYear(nsx),
                quantity: 0,
            } satisfies RowNew)
        } else {
            append({
                kind: 'existing' as const,
                product_lot_id: '',
                quantity: 0,
            } satisfies RowExisting)
        }
    }

    const handleScannerOpenChange = useCallback((open: boolean) => {
        if (!open) setScannerRowIndex(null)
    }, [])

    const handleProductScanned = useCallback(
        (product: Product) => {
            if (scannerRowIndex === null) return
            // Inject SP vừa quét vào cache options để Combobox tìm được và
            // hiển thị tên SP trên trigger (nếu không, trigger show placeholder
            // do SP nằm ngoài top 50 của useProducts).
            setScannedProducts((prev) => {
                if (prev.has(product.id)) return prev
                const next = new Map(prev)
                next.set(product.id, {
                    value: product.id,
                    label: product.name,
                    unit: product.unit,
                    render: () => renderProductOption(product),
                })
                return next
            })
            setValue(
                `${fieldName}.${scannerRowIndex}.new_lot_product_id`,
                product.id,
                { shouldDirty: true, shouldValidate: true },
            )
            // Autofill unit so the "Đơn vị" hint below the picker matches the
            // scanned product without a manual re-click.
            setValue(
                `${fieldName}.${scannerRowIndex}.new_lot_product_unit`,
                product.unit,
                { shouldDirty: true },
            )
            setScannerRowIndex(null)
        },
        [fieldName, scannerRowIndex, setValue],
    )

    // Không tìm thấy sản phẩm khớp mã vạch: báo rõ để thủ kho biết mã chưa
    // gán (nhiều SP chưa có barcode) và chọn tay, thay vì đóng dialog im lặng.
    const handleBarcodeNotFound = useCallback((barcode: string) => {
        toast.error(m.pos_barcode_not_found_desc({ barcode }))
    }, [])

    // Thanh nút "Thêm lô" + "Quét mã" đặt DƯỚI danh sách item: user thêm rồi sửa
    // ngay tại chỗ, không phải kéo lên đầu form mỗi lần muốn thêm dòng.
    const actionBar = (
        <div className="flex items-center gap-2">
            {headerAction}
            {(maxItems === undefined || fields.length < maxItems) && (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addRow}
                >
                    <Plus className="size-4 mr-1" />
                    {m.lot_items_add()}
                </Button>
            )}
        </div>
    )

    return (
        <div className="space-y-3">
            <Label>{m.lot_items_title()}</Label>

            {fields.length === 0 && (
                <div className="rounded-md border border-dashed bg-muted/30 py-6 text-center text-sm text-muted-foreground">
                    {m.lot_items_empty()}
                </div>
            )}

            {fields.map((field, idx) => {
                const kind = watch(`${fieldName}.${idx}.kind`) as
                    | 'new'
                    | 'existing'
                return (
                    <div
                        key={field.id}
                        className="grid gap-3 rounded-md border bg-card p-3 min-w-0"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                                {m.lot_items_row({ n: idx + 1 })}
                            </span>
                            <div className="flex items-center gap-2">
                                {mode === 'in' && (
                                    <div className="flex rounded-md border p-0.5 text-sm md:text-xs">
                                        <button
                                            type="button"
                                            className={`min-h-11 rounded px-3 py-2 md:min-h-0 md:px-2 md:py-1 ${kind === 'new' ? 'bg-primary text-primary-foreground' : ''}`}
                                            onClick={() =>
                                                update(idx, {
                                                    kind: 'new' as const,
                                                    new_lot_product_id: '',
                                                    // '' để superRefine nguồn
                                                    // vẫn chạy (xem new.tsx).
                                                    new_lot_position_id: '',
                                                    quantity: 0,
                                                } satisfies RowNew)
                                            }
                                        >
                                            {m.lot_items_new_lot()}
                                        </button>
                                        <button
                                            type="button"
                                            className={`min-h-11 rounded px-3 py-2 md:min-h-0 md:px-2 md:py-1 ${kind === 'existing' ? 'bg-primary text-primary-foreground' : ''}`}
                                            onClick={() =>
                                                update(idx, {
                                                    kind: 'existing' as const,
                                                    product_lot_id: '',
                                                    quantity: 0,
                                                } satisfies RowExisting)
                                            }
                                        >
                                            {m.lot_items_existing_lot()}
                                        </button>
                                    </div>
                                )}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => remove(idx)}
                                >
                                    <Trash2 className="size-4 text-destructive" />
                                </Button>
                            </div>
                        </div>

                        {mode === 'in' && kind === 'new' && (
                            <div className="grid gap-3 md:grid-cols-3">
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_code_label()}
                                    </Label>
                                    <Input
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_code`,
                                        )}
                                        placeholder="SP-LOT-..."
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_code`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.product_lot_new_lot_product_label()}
                                    </Label>
                                    <div className="flex gap-2">
                                        <Combobox
                                            className="min-w-0 flex-1"
                                            options={mergedProductOptions}
                                            value={watch(
                                                `${fieldName}.${idx}.new_lot_product_id`,
                                            )}
                                            onChange={(v) => {
                                                setValue(
                                                    `${fieldName}.${idx}.new_lot_product_id`,
                                                    v,
                                                )
                                                // Autofill unit from the picked
                                                // product so downstream rows /
                                                // reports don't need to
                                                // re-lookup productOptions.
                                                const opt =
                                                    mergedProductOptions.find(
                                                        (o) => o.value === v,
                                                    )
                                                setValue(
                                                    `${fieldName}.${idx}.new_lot_product_unit`,
                                                    opt?.unit ?? '',
                                                )
                                                // Giữ SP đã chọn tay trong cache
                                                // để nó không biến mất khỏi
                                                // trigger khi search dùng chung
                                                // đổi (pick ở row khác / refetch).
                                                if (opt) cacheProduct(opt)
                                            }}
                                            onSearchChange={
                                                onProductSearchChange
                                            }
                                            shouldFilter={
                                                !onProductSearchChange
                                            }
                                            onOpenChange={
                                                onProductSearchChange
                                                    ? (open) => {
                                                          if (!open)
                                                              onProductSearchChange(
                                                                  '',
                                                              )
                                                      }
                                                    : undefined
                                            }
                                            placeholder={m.product_lot_new_lot_product_label()}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            className="shrink-0"
                                            onClick={() =>
                                                setScannerRowIndex(idx)
                                            }
                                            aria-label={m.mobile_scan_barcode_button()}
                                            title={m.mobile_scan_barcode_button()}
                                        >
                                            <Camera className="size-4" />
                                        </Button>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {m.order_unit_col()}:{' '}
                                        {watch(
                                            `${fieldName}.${idx}.new_lot_product_unit`,
                                        ) ||
                                            (watch(
                                                `${fieldName}.${idx}.new_lot_product_id`,
                                            )
                                                ? '—'
                                                : '—')}
                                    </p>
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_product_id`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.lot_position_label()}
                                    </Label>
                                    <PositionPicker
                                        lotType="product"
                                        value={watch(
                                            `${fieldName}.${idx}.new_lot_position_id`,
                                        )}
                                        onChange={(v) =>
                                            setValue(
                                                `${fieldName}.${idx}.new_lot_position_id`,
                                                v,
                                            )
                                        }
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_position_id`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_manufactured_label()}
                                    </Label>
                                    <Input
                                        type="date"
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_manufactured_date`,
                                            {
                                                onChange: (e) => {
                                                    // Auto-nhảy HSD = NSX +
                                                    // 1 năm khi user đổi NSX.
                                                    // User vẫn có thể sửa
                                                    // HSD sau — sync chỉ chạy
                                                    // tại thời điểm sửa NSX.
                                                    const nsx = e.target.value
                                                    setValue(
                                                        `${fieldName}.${idx}.new_lot_expiry_date`,
                                                        nsx
                                                            ? addOneYear(nsx)
                                                            : '',
                                                        {
                                                            shouldValidate: true,
                                                            shouldDirty: true,
                                                        },
                                                    )
                                                },
                                            },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_manufactured_date`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_lot_expiry_label()}
                                    </Label>
                                    <Input
                                        type="date"
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_expiry_date`,
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_expiry_date`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_quantity_label()}
                                    </Label>
                                    <Input
                                        type="number"
                                        step="0.001"
                                        min={0}
                                        {...register(
                                            `${fieldName}.${idx}.quantity`,
                                            { valueAsNumber: true },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.quantity`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.product_lot_discount_label()}
                                    </Label>
                                    {/* Tuỳ chọn: bỏ trống = lô không đánh dấu.
                                        setValueAs thay cho valueAsNumber vì
                                        valueAsNumber biến ô rỗng thành NaN,
                                        làm zod chặn submit dù field optional.
                                        step="any" để gõ thập phân tự do; chốt
                                        làm tròn 2 số ở bước gửi API. */}
                                    <Input
                                        type="number"
                                        step="any"
                                        min={0}
                                        max={100}
                                        placeholder="0"
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_discount_percent`,
                                            {
                                                setValueAs:
                                                    parseLotDiscountInput,
                                            },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_discount_percent`}
                                    />
                                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                                        {m.product_lot_discount_hint()}
                                    </p>
                                </div>
                            </div>
                        )}

                        {(mode === 'out' ||
                            (mode === 'in' && kind === 'existing')) && (
                            <div className="grid gap-3 md:grid-cols-2">
                                <div className="min-w-0">
                                    <Label className="text-xs">
                                        {m.lot_items_select_lot()}
                                    </Label>
                                    <Combobox
                                        options={mergedLotOptions.filter(
                                            (o) => {
                                                const current =
                                                    selectedLotIds[idx]
                                                if (o.value === current)
                                                    return true
                                                return !selectedLotIds.some(
                                                    (id, i) =>
                                                        i !== idx &&
                                                        id === o.value,
                                                )
                                            },
                                        )}
                                        value={watch(
                                            `${fieldName}.${idx}.product_lot_id`,
                                        )}
                                        onChange={(v) => {
                                            setValue(
                                                `${fieldName}.${idx}.product_lot_id`,
                                                v,
                                            )
                                            const opt = mergedLotOptions.find(
                                                (o) => o.value === v,
                                            )
                                            if (opt) cacheLot(opt)
                                        }}
                                        onSearchChange={onLotSearchChange}
                                        shouldFilter={
                                            onLotSearchChange ? false : true
                                        }
                                        onOpenChange={
                                            onLotSearchChange
                                                ? (open) => {
                                                      if (!open)
                                                          onLotSearchChange('')
                                                  }
                                                : undefined
                                        }
                                        placeholder={m.lot_items_lot_picker_placeholder()}
                                        searchPlaceholder={m.lot_items_lot_picker_search()}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.product_lot_id`}
                                    />
                                    {mode === 'out' && (
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {(() => {
                                                const lotId = watch(
                                                    `${fieldName}.${idx}.product_lot_id`,
                                                )
                                                const lot =
                                                    mergedLotOptions.find(
                                                        (l) =>
                                                            l.value === lotId,
                                                    )
                                                if (
                                                    lot &&
                                                    typeof lot.available ===
                                                        'number'
                                                ) {
                                                    return m.lot_items_available_hint(
                                                        {
                                                            qty: formatQty(
                                                                lot.available,
                                                            ),
                                                            unit:
                                                                lot.unit ?? '',
                                                        },
                                                    )
                                                }
                                                return null
                                            })()}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.pkl_new_quantity_label()}
                                    </Label>
                                    <Input
                                        type="number"
                                        step="0.001"
                                        min={0}
                                        {...register(
                                            `${fieldName}.${idx}.quantity`,
                                            { valueAsNumber: true },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.quantity`}
                                    />
                                </div>
                            </div>
                        )}

                        {renderExtraPerItem?.(idx)}
                    </div>
                )
            })}

            {actionBar}

            <MobileBarcodeScannerDialog
                open={scannerOpen}
                onOpenChange={handleScannerOpenChange}
                onProductFound={handleProductScanned}
                onBarcodeNotFound={handleBarcodeNotFound}
            />
        </div>
    )
}
